import {Component, Input, OnInit, ViewChild} from '@angular/core';
import {
  Contact, ContactService, CustomerContactService,
  DateUtils,
  Insurance,
  InsuranceCategoryService,
  InsuranceProvider,
  InsuranceProviderService,
  InsuranceService,
  InsuranceSubscription,
  InsuranceSubscriptionSearch,
  InsuranceSubscriptionService,
  InsuranceSubscriptionStatus,
  ProductProviderService,
  ProductService, Ref,
  SearchResult,
  SigningPackageService,
  SigningPackageStatus,
  SimplePaginationUtils,
  Subscription,
  SubscriptionFile,
  SubscriptionFileService,
  SubscriptionSearch,
  SubscriptionService,
  SubscriptionSigningPackage,
  SubscriptionSigningPackageSearch,
  SubscriptionSigningPackageService,
} from '@lifeislife/lifeislife-domain';
import {BehaviorSubject, combineLatest, forkJoin, Observable, of} from 'rxjs';
import {map, publishReplay, refCount, switchMap} from 'rxjs/operators';
import {Router} from '@angular/router';
import {CustomerContextService} from '../../service/customer-context-service';
import {FileDownloadComponent} from '@lifeislife/lifeislife-components';
import {animate, state, style, transition, trigger} from '@angular/animations';

@Component({
  selector: 'cf-insurance-subscription-item',
  templateUrl: './insurance-subscription-item.component.html',
  styleUrls: ['./insurance-subscription-item.component.scss'],
  animations: [
    trigger('detailsCollapsed', [
      state('true', style({
        height: '0',
      })),
      state('false', style({
        height: 'auto',
      })),
      transition('true => *', [
        style({height: '0'}),
        animate(200),
        style({height: 'auto'}),
      ]),
    ]),
  ],
})
export class InsuranceSubscriptionItemComponent implements OnInit {

  @Input()
  set insuranceSubscription(value: InsuranceSubscription) {
    this.insuranceSubscriptionSource$.next(value);
  }

  @Input()
  showLinked = true;
  @Input()
  showSubTitle = true;
  @Input()
  showFooter = true;
  @Input()
  showDetailsInfo = true;
  @Input()
  showDetailsReserves = true;
  @Input()
  showDetailsProvider = true;
  @Input()
  showDetails = false;
  @Input()
  showEmptyActions = false;
  @Input()
  headerCollapsable = true;

  insuranceSubscriptionSource$ = new BehaviorSubject<InsuranceSubscription>(null);

  providerName$: Observable<string>;
  providerProductName$: Observable<string>;
  providerReference$: Observable<string>;
  insuranceProvider$: Observable<InsuranceProvider>;
  categoryname$: Observable<string>;
  internalReference$: Observable<string>;
  startDate$: Observable<Date>;
  birthdayDate$: Observable<Date>;
  status$: Observable<InsuranceSubscriptionStatus>;
  linkedSubscription$: Observable<InsuranceSubscription[]>;
  pricing$: Observable<number>;
  recurrenceLabel$: Observable<string>;
  signLink$: Observable<string | null>;
  lifeInsuranceReserveAmount$: Observable<number>;
  lifeInsuranceRetirementAmount$: Observable<number>;
  lifeInsuranceReserveImportDate$: Observable<Date>;
  insuredInfo$: Observable<string>;
  insuredContactName$: Observable<string>;
  deathInsuranceCoveredAmount$: Observable<string>;
  incomesGuaranteeCoveredAmount$: Observable<number>;
  childrenVisible = true;
  documentsVisible = true;
  documentsCount: number = null;
  updateDate$: Observable<Date>;

  @ViewChild(FileDownloadComponent, {static: true})
  private fileDownloadComponent: FileDownloadComponent;

  constructor(
    private insuranceService: InsuranceService,
    private insuranceProviderService: InsuranceProviderService,
    private providerService: ProductProviderService,
    private subscriptionService: SubscriptionService,
    private productService: ProductService,
    private insuranceCategoryService: InsuranceCategoryService,
    private subscriptionFileService: SubscriptionFileService,
    private subscriptionSigningPackageService: SubscriptionSigningPackageService,
    private signingPackageService: SigningPackageService,
    private customerContextService: CustomerContextService,
    private customerContactService: CustomerContactService,
    private contactService: ContactService,
    private insuranceSubscriptionService: InsuranceSubscriptionService,
    private router: Router,
  ) {
  }

  ngOnInit(): void {
    const insurance$ = this.insuranceSubscriptionSource$.pipe(
      switchMap(i => this.getInsuranceSubscriptionInsurance$(i)),
      publishReplay(1), refCount(),
    );
    this.insuranceProvider$ = insurance$.pipe(
      switchMap(i => this.getInsuranceProvider$(i)),
      publishReplay(1), refCount(),
    );
    const productProvider$ = this.insuranceProvider$.pipe(
      switchMap(p => this.getInsuranceProviderProductProvider$(p)),
      publishReplay(1), refCount(),
    );
    const product$ = insurance$.pipe(
      switchMap(i => this.getInsuranceProduct$(i)),
      publishReplay(1), refCount(),
    );
    const subscription$ = this.insuranceSubscriptionSource$.pipe(
      switchMap(i => this.getInsuranceSubscriptionSubscription$(i)),
      publishReplay(1), refCount(),
    );

    this.internalReference$ = subscription$.pipe(
      map(s => s.internalReference),
      publishReplay(1), refCount(),
    );
    this.providerName$ = productProvider$.pipe(
      map(p => p == null ? null : p.publicLabel),
      publishReplay(1), refCount(),
    );
    this.providerProductName$ = product$.pipe(
      map(p => p == null ? null : p.name),
      publishReplay(1), refCount(),
    );
    this.providerReference$ = subscription$.pipe(
      map(i => i == null ? null : i.providerReference),
      publishReplay(1), refCount(),
    );
    this.categoryname$ = insurance$.pipe(
      switchMap(i => this.getInsuranceCategoryName$(i)),
      publishReplay(1), refCount(),
    );
    this.startDate$ = subscription$.pipe(
      map(s => s == null ? null : s.startData),
      publishReplay(1), refCount(),
    );
    this.birthdayDate$ = subscription$.pipe(
      map(s => s == null ? null : this.subscriptionService.getSubscriptionNextBirthdayDate(s)),
      publishReplay(1), refCount(),
    );
    this.status$ = this.insuranceSubscriptionSource$.pipe(
      map(i => i == null ? null : i.insuranceSubscriptionStatus),
      publishReplay(1), refCount(),
    );
    this.pricing$ = subscription$.pipe(
      map(s => s == null ? null : s.totalPricing),
      publishReplay(1), refCount(),
    );
    this.recurrenceLabel$ = subscription$.pipe(
      map(s => s == null ? null : this.subscriptionService.getRecurrencelabel(s.recurrence)),
      publishReplay(1), refCount(),
    );

    this.signLink$ = this.insuranceSubscriptionSource$.pipe(
      switchMap(i => this.getSubscriptionSignLink$(i)),
      publishReplay(1), refCount(),
    );
    this.linkedSubscription$ = subscription$.pipe(
      switchMap(i => this.searchSubscriptionLinkedSubscriptions$(i)),
      publishReplay(1), refCount(),
    );
    this.updateDate$ = combineLatest([
      this.insuranceSubscriptionSource$.pipe(map(i => i == null ? null : i.vestedReserveAmountUpdateDate)),
      subscription$.pipe(map(s => s == null ? null : s.lastImportDateTime)),
      subscription$.pipe(map(s => s == null ? null : s.updateDateTime)),
    ]).pipe(
      map(r => DateUtils.getMaxDate(...r)),
    );

    this.lifeInsuranceReserveAmount$ = this.insuranceSubscriptionSource$.pipe(
      map(i => i == null ? null : i.vestedReserveAmount),
      publishReplay(1), refCount(),
    );
    this.lifeInsuranceRetirementAmount$ = this.insuranceSubscriptionSource$.pipe(
      map(i => i == null ? null : i.estimatedRetirementAmount),
      publishReplay(1), refCount(),
    );
    this.lifeInsuranceReserveImportDate$ = this.insuranceSubscriptionSource$.pipe(
      map(i => i == null ? null : i.vestedReserveAmountUpdateDate),
      publishReplay(1), refCount(),
    );
    this.insuredInfo$ = this.insuranceSubscriptionSource$.pipe(
      map(i => i == null ? null : i.insuredInfo),
      publishReplay(1), refCount(),
    );
    this.insuredContactName$ = this.insuranceSubscriptionSource$.pipe(
      map(i => i == null ? null : i.insuredCustomerContactRef),
      switchMap(ref => ref == null ? of(null) : this.customerContactService.getCustomerContact$(ref)),
      switchMap(cc => cc == null ? of(null) : this.contactService.getContact$(cc.contactRef)),
      map(c => c == null ? null : this.contactService.getContactName(c)),
      publishReplay(1), refCount(),
    );
    this.deathInsuranceCoveredAmount$ = this.insuranceSubscriptionSource$.pipe(
      map(i => i == null ? null : i.deathInsuranceCoveredAmount),
      publishReplay(1), refCount(),
    );
    this.incomesGuaranteeCoveredAmount$ = this.insuranceSubscriptionSource$.pipe(
      map(i => i == null ? null : i.incomesGuaranteeCoveredAmount),
      publishReplay(1), refCount(),
    );
  }

  onDetailsClick(event: MouseEvent) {
    const insuranceSubscription = this.insuranceSubscriptionSource$.getValue();
    this.router.navigate(['/product', insuranceSubscription.id]);
  }

  toggleChildrenVisible() {
    this.childrenVisible = !this.childrenVisible;
  }


  onHeaderClick() {
    if (this.headerCollapsable) {
      this.showDetails = !this.showDetails;
    }
  }

  private getInsuranceSubscriptionInsurance$(insuranceSubscription: InsuranceSubscription) {
    if (insuranceSubscription == null) {
      return of(null);
    }
    return this.insuranceService.getInsurance$(insuranceSubscription.insuranceRef);
  }

  private getInsuranceProvider$(insurance: Insurance) {
    if (insurance == null) {
      return of(null);
    }
    return this.insuranceProviderService.getInsuranceProvider$(insurance.insuranceProviderRef);
  }


  private getInsuranceProviderProductProvider$(insuranceProvider: InsuranceProvider) {
    if (insuranceProvider == null) {
      return of(null);
    }
    return this.providerService.getProductProvider$(insuranceProvider.productProviderRef);
  }

  private getInsuranceProduct$(insurance: Insurance) {
    if (insurance == null) {
      return null;
    }
    return this.productService.getProduct$(insurance.productRef);
  }

  private getInsuranceSubscriptionSubscription$(insuranceSubscription: InsuranceSubscription) {
    if (insuranceSubscription == null) {
      return of(null);
    }
    return this.subscriptionService.getSubscription$(insuranceSubscription.subscriptionRef);
  }

  private getInsuranceCategoryName$(insurance: Insurance) {
    if (insurance == null) {
      return of(null);
    }
    return this.insuranceCategoryService.getInsuranceCategory$(insurance.insuranceCategoryRef).pipe(
      map(c => c == null ? null : c.publicLabel),
    );
  }

  private getSubscriptionSignLink$(insuranceSubscription: InsuranceSubscription) {
    if (insuranceSubscription == null
      || insuranceSubscription.insuranceSubscriptionStatus !== InsuranceSubscriptionStatus.MANDATE_SIGNATURE_PENDING) {
      return of(null);
    }
    const customerRef = this.customerContextService.getActiveCustomerRef();
    const searchFilter: SubscriptionSigningPackageSearch = {
      subscriptionSearch: {
        subscriptionRef: insuranceSubscription.subscriptionRef,
        customerSearch: {
          exactCustomerRef: customerRef,
        },
      },
      signingPackageSearch: {
        anyStatusList: [SigningPackageStatus.WAIITING_SIGNATURE],
        customerSearch: {
          exactCustomerRef: customerRef,
        },
      },
    };
    const pagination = SimplePaginationUtils.newPagination(1);
    return this.subscriptionSigningPackageService.searchSubscriptionSigningPackages$(searchFilter, pagination).pipe(
      switchMap(result => this.getSingleSigningLink$(result)),
    );
  }


  private getSingleSigningLink$(result: SearchResult<SubscriptionSigningPackage>) {
    if (result == null || result.count === 0 || result.list.length === 0) {
      return of(null);
    }
    const subSigningPackage = result.list[0];
    const contactRef = this.customerContextService.getActiveContactRef();
    return of(this.signingPackageService.getSignerSignUrl(subSigningPackage.signingPackageRef, contactRef));
  }

  private searchSubscriptionLinkedSubscriptions$(subscription: Subscription) {
    const hasParent = subscription.parentSubscriptionRef != null;
    const customerRef = this.customerContextService.getActiveCustomerRef();
    const subscriptionSearch: SubscriptionSearch = {
      customerSearch: {
        exactCustomerRef: customerRef,
      },
      active: true,
    };
    if (hasParent) {
      subscriptionSearch.subscriptionRef = subscription.parentSubscriptionRef;
    } else {
      subscriptionSearch.parentSubscriptionRef = {id: subscription.id};
    }
    const insuranceSubscroiptionSearch: InsuranceSubscriptionSearch = {
      subscriptionSearch: subscriptionSearch,
    };
    const pagination = SimplePaginationUtils.newPagination(20);
    return this.insuranceSubscriptionService.searchInsuranceSubscriptions$(insuranceSubscroiptionSearch, pagination).pipe(
      map(r => r.list),
    );
  }

  onDownloadFileClick(file: SubscriptionFile) {
    forkJoin([
      this.subscriptionFileService.getFileDownloadUrl$(file, 'attachment'),
      this.subscriptionFileService.getStoredFile$(file),
    ]).subscribe(r => this.fileDownloadComponent.downloadFile(r[0], r[1].fileName));
  }

}
